import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConsultantFormData } from '@/types/consultant-registration.types';
import { TOTAL_STEPS } from '@/constants/consultant-registration.constants';
import { validateStep, scrollToFirstError } from '@/utils/consultant-registration-validation';
import {
  saveFormToLocalStorage,
  loadFormFromLocalStorage,
  clearFormFromLocalStorage,
} from '@/services/consultant-registration.service';
import { auth, db } from '@/lib/firebase';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { StepIndicator } from '@/components/broker-registration/StepIndicator';
import { NavigationButtons } from '@/components/broker-registration/NavigationButtons';
import { StepOne } from '@/components/consultant-registration/steps/StepOne';
import { StepTwo } from '@/components/consultant-registration/steps/StepTwo';
import { StepThree } from '@/components/consultant-registration/steps/StepThree';
import { StepFour } from '@/components/consultant-registration/steps/StepFour';
import { LanguageSelector } from '@/components/ui/language-selector';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';
import {
  addRoleToCurrentUser,
  assignRoleViaNestJS,
  EMAIL_ALREADY_IN_USE_ROLE_MESSAGE,
  getErrorMessage,
  isEmailAlreadyInUseError,
} from '@/lib/registration-roles';
import { DiditVerification, type EntityType } from '@/components/verification/DiditVerification'; // NOUVEAU

// Initial form data
const getInitialFormData = (): ConsultantFormData => ({
  currentStep: 1,
  // Step 1: Account & Address
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
  confirmPassword: '',
  entityType: 'individual', // NOUVEAU
  addressSearch: '',
  streetAddress: '',
  city: '',
  stateProvince: '',
  zipPostalCode: '',
  country: '',
  institutionTypes: [],
  howHeard: '',
  motherTongue: '',
  spokenLanguages: [],
  // Step 2: Profile & Expertise
  companyName: '',
  specializations: [{ industry: '', expertises: [], experienceYears: '' }],
  hasLicense: '',
  licenses: [],
  // Step 3: Services & Compensation
  services: '',
  compensationOptions: [],
  currencies: [],
  hourlyRate: '',
  hourlyRateCurrency: '',
  projectRateDetails: '',
  equityPartnershipDetails: '',
  availability: '',
  // Step 4: Compliance
  termsAccepted: false,
  backgroundCheckAuth: false,
});

export default function RegisterConsultant() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ConsultantFormData>(getInitialFormData());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load saved draft on mount
  useEffect(() => {
    const savedData = loadFormFromLocalStorage();
    if (savedData && savedData.formData) {
      setFormData(savedData.formData);
      setCurrentStep(savedData.step);
    }
  }, []);

  // Save to localStorage on formData or step change — seulement pendant
  // le remplissage du formulaire (étapes 1 à TOTAL_STEPS). Une fois le
  // compte créé (étape 5, vérification Didit), il n'y a plus de brouillon
  // à conserver — le sauvegarder à nouveau ici annulerait le
  // clearFormFromLocalStorage() fait dans handleSubmit et forcerait
  // l'utilisateur à revivre l'étape 5 à chaque nouvelle visite de la page.
  useEffect(() => {
    if (currentStep > TOTAL_STEPS) return;
    if (currentStep > 1 || Object.values(formData).some((v) => v !== '')) {
      saveFormToLocalStorage(formData, currentStep);
    }
  }, [formData, currentStep]);

  const updateFormData = useCallback((updates: Partial<ConsultantFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const clearError = useCallback((field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  const handleStepValidation = useCallback(
    (step: number): boolean => {
      const validation = validateStep(step, formData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        scrollToFirstError();
        return false;
      }
      setErrors({});
      return true;
    },
    [formData]
  );

  const goToStep = useCallback(
    (targetStep: number) => {
      if (targetStep <= currentStep) {
        // Can go back without validation
        setCurrentStep(targetStep);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Going forward requires validation
        if (handleStepValidation(currentStep)) {
          setCurrentStep(targetStep);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    },
    [currentStep, handleStepValidation]
  );

  const handleNext = useCallback(() => {
    if (handleStepValidation(currentStep) && currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, handleStepValidation]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    // Final validation
    if (!handleStepValidation(TOTAL_STEPS)) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Ce bloc est inchangé — création du profil consultant
      const consultantProfile = {
        email: formData.email,
        displayName: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone || '',
        companyName: formData.companyName || '',
        status: 'pending_review',
        language,
        registrationData: (() => { const { password: _p, confirmPassword: _c, profilePhoto: _pp, ...rest } = formData as any; return rest; })(),
        updatedAt: serverTimestamp(),
      };

      try {
        // 1. Create Firebase Auth user
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email,
          formData.password
        );
        const user = userCredential.user;

        await signInWithEmailAndPassword(auth, formData.email, formData.password);

        await updateProfile(user, {
          displayName: `${formData.firstName} ${formData.lastName}`,
        });

        // 2. Save profile to Firestore
        await setDoc(doc(db, 'users', user.uid), {
          ...consultantProfile,
          roles: ['consultant'],
          createdAt: serverTimestamp(),
        }, { merge: true });

        await assignRoleViaNestJS(user, 'consultant');
      } catch (error: unknown) {
        if (!isEmailAlreadyInUseError(error)) throw error;
        await addRoleToCurrentUser({ email: formData.email, password: formData.password, role: 'consultant', profileUpdates: consultantProfile });
      }

      clearFormFromLocalStorage();

      // MODIFIÉ — plus d'envoi d'email ici : le welcome email
      // definitif sera géré une fois la révision staff complétée
      // (voir diditWebhook.ts). L'utilisateur avance maintenant vers
      // la vérification Didit au lieu d'un écran de succès immédiat.

      toast({
        title: 'Inscription réussie !',
        description: "Passons à la vérification d'identité.",
      });

      setCurrentStep(5); // NOUVEAU — étape de vérification Didit
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error: unknown) {
      console.error('Registration error:', error);
      const message =
        isEmailAlreadyInUseError(error)
          ? EMAIL_ALREADY_IN_USE_ROLE_MESSAGE
          : getErrorMessage(error, 'Une erreur est survenue.');
      toast({
        title: 'Erreur',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, handleStepValidation, navigate, language, toast]);

  // NOUVEAU — après approbation Didit, direction la page de paiement
  // consultant (comme pour Partner : le paiement n'attend pas la
  // révision staff, seule l'activation finale du compte l'attendra)
  const handleVerificationApproved = useCallback(() => {
    navigate('/consultant/payment');
  }, [navigate]);

  const handleVerificationDeclined = useCallback(() => {
    // DiditVerification affiche déjà son propre écran "refusé"
  }, []);

  // Render current step
  const renderCurrentStep = () => {
    const stepProps = {
      formData,
      updateFormData,
      errors,
      clearError,
    };

    switch (currentStep) {
      case 1:
        return <StepOne {...stepProps} />;
      case 2:
        return <StepTwo {...stepProps} />;
      case 3:
        return <StepThree {...stepProps} />;
      case 4:
        return <StepFour {...stepProps} />;
      default:
        return <StepOne {...stepProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 py-8 px-4">
      {/* Logo */}
      <div className="flex justify-center mb-6">
        <a href="/">
          <img src="/images/acquizgroup-logo.png" alt="AcquizGroup" className="h-12 object-contain" />
        </a>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Header Card - Navy */}
        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] rounded-t-xl p-6 shadow-lg">
          <div className="flex items-center justify-between gap-4 mb-2">
            <div className="flex-shrink-0 w-32"></div>
            <h1 className="text-2xl md:text-3xl font-bold text-white text-center">
              {t('consultant_registration.page_title')}
            </h1>
            <div className="flex-shrink-0 w-32 flex justify-end">
              <LanguageSelector />
            </div>
          </div>

          {/* MODIFIÉ — masqué pendant la vérification Didit (étape 5) */}
          {currentStep <= TOTAL_STEPS ? (
            <>
              <p className="text-white/70 text-center mt-2">
                {t('consultant_registration.steps.name')} {currentStep} {t('consultant_registration.steps.of')} {TOTAL_STEPS}
              </p>
              <div className="mt-6">
                <StepIndicator currentStep={currentStep} onStepClick={goToStep} />
              </div>
            </>
          ) : (
            <p className="text-white/70 text-center mt-2">
              {language === 'fr' ? "Vérification d'identité" : 'Identity Verification'}
            </p>
          )}
        </div>

        {/* Content Card - Light */}
        <div className="bg-white border border-slate-200 rounded-b-xl p-6 md:p-8 shadow-lg">
          {currentStep <= TOTAL_STEPS && (
            <form onSubmit={(e) => e.preventDefault()}>
              <div id="step-content">{renderCurrentStep()}</div>

              <NavigationButtons
                currentStep={currentStep}
                totalSteps={TOTAL_STEPS}
                onBack={handleBack}
                onNext={handleNext}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            </form>
          )}

          {/* NOUVEAU — Étape 5 : vérification d'identité (Didit) */}
          {currentStep === 5 && (
            <DiditVerification
              role="consultant"
              entityType={formData.entityType}
              onApproved={handleVerificationApproved}
              onDeclined={handleVerificationDeclined}
            />
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="text-center mt-6">
        <p className="text-slate-500 text-sm">
          Déjà membre ?{' '}
          <a href="/login" className="text-[#1D314D] hover:text-[#CEAB65] font-medium">
            Connexion
          </a>
        </p>
      </div>
    </div>
  );
}